import { localizedText } from "../helpers/localization"
import { Controller } from "@hotwired/stimulus"

const MAX_TITLE_LENGTH = 100
const MEASUREMENT_TOLERANCE = 2

export default class extends Controller {
  static targets = [
    "title", "counter", "fieldError", "typeTab", "deviceTab", "normalControls", "previewPanel",
    "placementTitle", "measurement", "status", "statusHeading", "statusDescription", "placementResults",
    "summary", "thumbnailInput", "thumbnailImage", "thumbnailPlaceholder", "thumbnailError", "thumbnailActions"
  ]

  connect() {
    this.videoType = "normal"
    this.device = "desktop"
    this.touched = false
    this.measurements = new Map()
    this.resizeObserver = new ResizeObserver(() => this.#scheduleMeasurement())
    this.resizeObserver.observe(this.element)
    document.fonts?.ready.then(() => this.#scheduleMeasurement())
    this.#updateInterface()
  }

  disconnect() {
    this.resizeObserver.disconnect()
    this.#revokeThumbnailUrl()
    cancelAnimationFrame(this.measurementFrame)
    clearTimeout(this.announcementTimer)
  }

  titleChanged() {
    this.touched = true
    this.titleTarget.value = this.titleTarget.value.replace(/[\r\n]+/g, " ")
    this.#updateInterface()
  }

  selectVideoType(event) {
    this.videoType = event.currentTarget.dataset.titlePreviewType
    this.#updateInterface()
  }

  selectDevice(event) {
    this.device = event.currentTarget.dataset.titlePreviewDevice
    this.#updateInterface()
  }

  chooseThumbnail() {
    this.thumbnailInputTarget.click()
  }

  thumbnailSelected(event) {
    this.#setThumbnail(event.target.files[0])
  }

  thumbnailFailed() {
    this.removeThumbnail()
    this.thumbnailErrorTarget.textContent = localizedText(this.element, "thumbnail_preview_failed")
  }

  dragOver(event) {
    event.preventDefault()
    event.currentTarget.classList.add("title-preview-drop-active")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("title-preview-drop-active")
  }

  dropThumbnail(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("title-preview-drop-active")
    this.#setThumbnail(event.dataTransfer.files[0])
  }

  removeThumbnail() {
    this.#revokeThumbnailUrl()
    this.thumbnailInputTarget.value = ""
    this.thumbnailActionsTarget.classList.add("hidden")
    this.thumbnailErrorTarget.textContent = ""
    this.thumbnailImageTargets.forEach((image) => {
      image.removeAttribute("src")
      image.classList.add("hidden")
    })
    this.thumbnailPlaceholderTargets.forEach((placeholder) => placeholder.classList.remove("hidden"))
  }

  reset() {
    this.titleTarget.value = ""
    this.touched = false
    this.videoType = "normal"
    this.device = "desktop"
    this.removeThumbnail()
    this.#updateInterface()
    this.titleTarget.focus()
  }

  #setThumbnail(file) {
    this.thumbnailErrorTarget.textContent = ""
    if (!file || !["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      this.thumbnailErrorTarget.textContent = localizedText(this.element, "invalid_image_type")
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      this.thumbnailErrorTarget.textContent = localizedText(this.element, "image_too_large")
      return
    }

    this.#revokeThumbnailUrl()
    this.thumbnailUrl = URL.createObjectURL(file)
    this.thumbnailImageTargets.forEach((image) => {
      image.src = this.thumbnailUrl
      image.classList.remove("hidden")
    })
    this.thumbnailPlaceholderTargets.forEach((placeholder) => placeholder.classList.add("hidden"))
    this.thumbnailActionsTarget.classList.remove("hidden")
  }

  #revokeThumbnailUrl() {
    if (!this.thumbnailUrl) return

    URL.revokeObjectURL(this.thumbnailUrl)
    this.thumbnailUrl = null
  }

  #updateInterface() {
    const title = this.titleTarget.value
    const count = this.#graphemeCount(title)
    this.counterTarget.textContent = `${count} / ${MAX_TITLE_LENGTH}`
    this.fieldErrorTarget.textContent = count > MAX_TITLE_LENGTH ? localizedText(this.element, "title_over_limit", { count: count - MAX_TITLE_LENGTH }) : ""
    this.fieldErrorTarget.classList.toggle("hidden", count <= MAX_TITLE_LENGTH)
    this.counterTarget.classList.toggle("text-red-300", count > MAX_TITLE_LENGTH)

    this.typeTabTargets.forEach((tab) => this.#setSelected(tab, tab.dataset.titlePreviewType === this.videoType))
    this.deviceTabTargets.forEach((tab) => this.#setSelected(tab, tab.dataset.titlePreviewDevice === this.device))
    this.normalControlsTargets.forEach((control) => control.classList.toggle("hidden", this.videoType !== "normal"))
    this.previewPanelTargets.forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.titlePreviewType !== this.videoType || panel.dataset.titlePreviewDevice !== this.device)
    })
    this.placementTitleTargets.forEach((element) => {
      element.textContent = title || localizedText(this.element, "title_placeholder")
      element.classList.toggle("title-preview-placeholder", title === "")
    })
    this.#scheduleMeasurement()
  }

  #setSelected(tab, selected) {
    tab.classList.toggle("title-preview-selected", selected)
    tab.setAttribute("aria-selected", String(selected))
  }

  #scheduleMeasurement() {
    cancelAnimationFrame(this.measurementFrame)
    this.measurementFrame = requestAnimationFrame(() => this.#measureTitles())
  }

  #measureTitles() {
    const title = this.titleTarget.value
    const count = this.#graphemeCount(title)
    this.measurements.clear()

    this.measurementTargets.forEach((element) => {
      const id = element.dataset.titlePreviewPlacement
      const lines = Number(element.dataset.titlePreviewLines)
      element.textContent = title || localizedText(this.element, "title_placeholder")
      const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight)
      const naturalHeight = element.scrollHeight
      const allowedHeight = lineHeight * lines
      const state = title === "" ? "empty_title" : count > MAX_TITLE_LENGTH ? "over_100_characters" : naturalHeight > allowedHeight + MEASUREMENT_TOLERANCE ? "cut_off" : "fully_visible"
      this.measurements.set(id, state)
    })

    this.#renderResults(title, count)
  }

  #renderResults(title, count) {
    if (!this.touched && title === "") {
      this.statusTarget.classList.add("hidden")
      this.summaryTarget.classList.add("hidden")
      return
    }

    this.statusTarget.classList.remove("hidden")
    this.summaryTarget.classList.remove("hidden")
    const selectedMeasurements = [...this.measurements.entries()].filter(([id]) => id.startsWith(`${this.videoType}-${this.device}-`))
    const hasCutoff = selectedMeasurements.some(([, state]) => state === "cut_off")

    if (count > MAX_TITLE_LENGTH) {
      this.statusHeadingTarget.textContent = localizedText(this.element, "title_limit_heading")
      this.statusDescriptionTarget.textContent = localizedText(this.element, "remove_characters", { count: count - MAX_TITLE_LENGTH })
    } else if (title === "") {
      this.statusHeadingTarget.textContent = localizedText(this.element, "title_empty_heading")
      this.statusDescriptionTarget.textContent = localizedText(this.element, "title_empty_description")
    } else if (hasCutoff) {
      this.statusHeadingTarget.textContent = localizedText(this.element, "title_cutoff_heading")
      this.statusDescriptionTarget.textContent = localizedText(this.element, "title_cutoff_description")
    } else {
      this.statusHeadingTarget.textContent = localizedText(this.element, "title_visible_heading")
      this.statusDescriptionTarget.textContent = localizedText(this.element, "title_visible_description")
    }

    this.#renderList(this.placementResultsTarget, selectedMeasurements)
    this.#renderList(this.summaryTarget.querySelector("[data-title-preview-summary-list]"), [...this.measurements.entries()])
    clearTimeout(this.announcementTimer)
    this.announcementTimer = setTimeout(() => this.statusTarget.setAttribute("aria-live", "polite"), 100)
  }

  #renderList(container, entries) {
    container.replaceChildren()
    entries.forEach(([id, state]) => {
      const row = document.createElement("li")
      const label = document.createElement("span")
      const badge = document.createElement("span")
      label.textContent = this.#placementLabel(id)
      badge.textContent = localizedText(this.element, state)
      badge.className = state === "cut_off" || state === "over_100_characters" ? "title-preview-status-bad" : "title-preview-status-good"
      row.className = "title-preview-result-row"
      row.append(label, badge)
      container.append(row)
    })
  }

  #placementLabel(id) {
    return id.replace(/^normal-|^short-/, "").split("-").map((part) => localizedText(this.element, part)).join(" ")
  }

  #graphemeCount(value) {
    return [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(value)].length
  }
}
